"use client";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement as _createElement } from "react";
import { useQueryClient, } from "@tanstack/react-query";
import { useCallback } from "react";
import { useListenNotificationStream } from "../NotificationStream/hooks";
import { useInfiniteNotifications, useMarkNotificationDelivered, useMarkNotificationSeen, } from "../hooks";
import { NotificationFormats, NotificationSortDirections, } from "../types";
import { ModalNotification } from "./ModalNotification";
import { ToastNotification } from "./ToastNotification";
const transientNotificationsQueryOptions = {
    unread: true,
    format: [
        NotificationFormats.TOAST,
        NotificationFormats.MODAL,
    ],
    sortDirection: NotificationSortDirections.ASC,
};
const transientNotificationQueryKey = useInfiniteNotifications.generateKey(transientNotificationsQueryOptions);
export const TransientNotificationsDisplayer = () => {
    var _a;
    const currentNotificationQuery = useInfiniteNotifications(Object.assign(Object.assign({}, transientNotificationsQueryOptions), { select: (notifications) => notifications.pages[0].find(({ seen }) => !seen) }));
    const queryClient = useQueryClient();
    useListenNotificationStream((newNotification) => {
        if (document.hidden || // don't show transient notifications if the tab is not visible
            !transientNotificationsQueryOptions.format.includes(newNotification.format)) {
            return;
        }
        queryClient.setQueriesData({
            queryKey: transientNotificationQueryKey,
        }, (oldData) => {
            if (oldData == null ||
                oldData.pages[0].some(({ id }) => id === newNotification.id)) {
                return oldData;
            }
            return Object.assign(Object.assign({}, oldData), { pages: [[...oldData.pages[0], newNotification]] });
        });
    });
    const markNotificationDeliveredMutation = useMarkNotificationDelivered(transientNotificationQueryKey);
    const markAsDelivered = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (currentNotificationQuery.data == null ||
            currentNotificationQuery.data.delivered) {
            return;
        }
        yield markNotificationDeliveredMutation.mutateAsync(currentNotificationQuery.data.id);
    }), [currentNotificationQuery.data, markNotificationDeliveredMutation]);
    const markNotificationSeenMutation = useMarkNotificationSeen(transientNotificationQueryKey);
    const markAsSeen = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (currentNotificationQuery.data == null ||
            currentNotificationQuery.data.seen) {
            return;
        }
        const notificationId = currentNotificationQuery.data.id;
        yield markNotificationSeenMutation.mutateAsync(notificationId, {
            onSettled: () => {
                // independently of the result, remove the notification from the list
                // to avoid showing it again
                queryClient.setQueriesData({
                    queryKey: transientNotificationQueryKey,
                }, (oldData) => {
                    if (oldData == null) {
                        return oldData;
                    }
                    return Object.assign(Object.assign({}, oldData), { pages: [
                            oldData.pages[0].filter(({ id }) => id !== notificationId),
                        ] });
                });
            },
        });
    }), [
        queryClient,
        currentNotificationQuery.data,
        markNotificationSeenMutation,
    ]);
    const ignore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        yield markAsDelivered();
        yield markAsSeen();
    }), [markAsSeen, markAsDelivered]);
    switch ((_a = currentNotificationQuery.data) === null || _a === void 0 ? void 0 : _a.format) {
        case NotificationFormats.TOAST:
            return (_createElement(ToastNotification, Object.assign({}, currentNotificationQuery.data, { key: currentNotificationQuery.data.id, onOpen: markAsDelivered, onClose: markAsSeen, onIgnore: ignore })));
        case NotificationFormats.MODAL:
            return (_createElement(ModalNotification, Object.assign({}, currentNotificationQuery.data, { key: currentNotificationQuery.data.id, onOpen: markAsDelivered, onClose: markAsSeen })));
        default:
            return null;
    }
};
TransientNotificationsDisplayer.prefetchQueries = (queryClient) => useInfiniteNotifications.prefetchQuery(queryClient, transientNotificationsQueryOptions);
//# sourceMappingURL=TransientNotificationsDisplayer.js.map